/**
 * Gestion des notifications push (Web Push API).
 * - Détecte le support navigateur.
 * - Demande la permission utilisateur.
 * - Enregistre le service worker, souscrit, et POST l'abonnement au backend.
 */
import { useCallback, useEffect, useState } from 'react';
import { portailApi } from '../services/portailApi';

const SW_PATH = '/sw.js';

type Permission = 'default' | 'granted' | 'denied' | 'unsupported';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
  return buf;
}

function isSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export interface UsePushResult {
  supported: boolean;
  permission: Permission;
  enabled: boolean;
  loading: boolean;
  enable: () => Promise<boolean>;
  disable: () => Promise<void>;
}

export function usePushNotifications(): UsePushResult {
  const supported = isSupported();
  const [permission, setPermission] = useState<Permission>(
    supported ? (Notification.permission as Permission) : 'unsupported'
  );
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  // Sync l'état d'abonnement existant au montage
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.register(SW_PATH);
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setEnabled(!!sub);
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const enable = useCallback(async (): Promise<boolean> => {
    if (!supported) return false;
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm as Permission);
      if (perm !== 'granted') return false;

      const reg = await navigator.serviceWorker.register(SW_PATH);
      await navigator.serviceWorker.ready;

      const { public_key } = await portailApi.getPushVapidKey();
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(public_key),
        });
      }

      await portailApi.pushSubscribe(sub.toJSON() as any);
      setEnabled(true);
      return true;
    } catch (e) {
      console.error('[Push] enable error', e);
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await portailApi.pushUnsubscribe(sub.endpoint);
        } catch { /* ignore */ }
        await sub.unsubscribe();
      }
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [supported]);

  return { supported, permission, enabled, loading, enable, disable };
}
